﻿import { ArrowLeft } from "lucide-react";
import { Link , useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { DashboardShell } from "@/components/dashboard-shell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuthenticatedMe } from "@/features/organizers/api";
import { useCreateShopMutation } from "@/features/shops/api";
import { ShopForm, type CreateShopFormValues } from "@/features/shops/shop-form";
import { ApiError } from "@/lib/api-client";
import { useT } from "@/lib/i18n";

export function NewShopPage() {
  const t = useT("shops");
  const navigate = useNavigate();
  const me = useAuthenticatedMe();
  const create = useCreateShopMutation();

  async function handleSubmit(values: CreateShopFormValues) {
    try {
      const shop = await create.mutateAsync({
        name: values.name,
        description: emptyToNull(values.description),
        slug: emptyToNull(values.slug),
        defaultLocale: values.defaultLocale,
        // themeColor UI'dan kaldırıldı (v2 tema ayarları bölümünde dönecek).
        themeColor: null,
        contactEmail: emptyToNull(values.contactEmail),
        contactPhone: emptyToNull(values.contactPhone),
        // Yeni mağazalar varsayılan olarak Profil hero'su kullanır;
        // organizer sonradan mağaza ayarları → Tema Ayarları'ndan değiştirebilir.
        // Site dışı tiplerde public render'a yansımaz ama DB'de kalır → Site'a
        // geçince otomatik geri gelir.
        heroStyle: "Profile",
        storefrontType: values.storefrontType,
      });
      toast.success(t("newPage.created", { name: shop.name }));
      navigate(`/shops/${shop.slug}`);
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.detail);
      } else {
        toast.error(t("newPage.createFailed"));
      }
    }
  }

  if (!me.data) return null;

  return (
    <DashboardShell companyName={me.data.companyName}>
      <div className="mb-6">
        <Button variant="ghost" size="sm" asChild className="mb-3 -ms-2">
          <Link to="/shops">
            <ArrowLeft className="me-1 h-4 w-4 rtl:-scale-x-100" />
            {t("title")}
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold">{t("new")}</h1>
        <p className="text-sm text-muted-foreground">{t("newPage.description")}</p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="text-lg">{t("newPage.cardTitle")}</CardTitle>
          <CardDescription>{t("newPage.cardDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          <ShopForm
            mode="create"
            defaultValues={{
              defaultLocale: me.data.preferredLocale ?? "tr",
              storefrontType: "Link",
            }}
            onSubmit={handleSubmit}
            isSubmitting={create.isPending}
          />
        </CardContent>
      </Card>
    </DashboardShell>
  );
}

function emptyToNull(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}
